import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getSvgByName } from '../Icon';
import styles from './Breadcrumb.module.css';
import c from 'classnames';

export type BreadcrumbSection = {
  label: string;
  href: string;
  icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
};

type Props = {
  parser: ({
    overrides,
  }: {
    overrides: Props['overrides'];
  }) => BreadcrumbSection[];
  onNavigate: (href: string) => void;
  overrides?: Record<string, string | undefined>;
  className?: string;
};

export function Breadcrumb({
  parser,
  onNavigate,
  overrides,
  className,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const sections = parser({ overrides });
  const ChevronIcon = getSvgByName('chevronForward');
  const [atLeft, setAtLeft] = useState(true);
  const [atRight, setAtRight] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);

  const labelsKey = sections.map(section => section.label).join(' > ');

  const updateScrollState = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const scrollable = el.scrollWidth > el.clientWidth;
    setIsScrollable(scrollable);
    setAtLeft(el.scrollLeft === 0);
    setAtRight(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    updateScrollState();

    el.addEventListener('scroll', updateScrollState);

    return () => {
      el.removeEventListener('scroll', updateScrollState);
    };
  }, [updateScrollState, labelsKey]);

  return (
    <div className={c(styles.wrapper, className)}>
      {isScrollable && !atLeft && <div className={styles.leftGradient} />}
      {isScrollable && !atRight && <div className={styles.rightGradient} />}
      <div className={styles.container} ref={containerRef}>
        {sections.map((section, index) => (
          <React.Fragment key={index}>
            <button
              className={styles.button}
              onClick={() => onNavigate(section.href)}
            >
              {section.icon ? <section.icon className={styles.icon} /> : null}
              {section.label}
            </button>
            {index + 1 !== sections.length && (
              <ChevronIcon className={styles.chevron} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
